import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Users, Compass, Briefcase, IndianRupee, TrendingUp, AlertTriangle, Calendar, Clock, Phone, MessageCircle, Mail, ChevronRight, X, Filter, Search, Loader2, CheckCircle2, Sparkles, FileText, MapPin } from "lucide-react";
import "./crm.css";
import CreateLeadModal from "./CreateLeadModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERTICAL_STYLE = {
  yatra: { color: "#B45309", bg: "#FEF3C7", icon: <Compass size={14} />, label: "Yatra" },
  jobs:  { color: "#1D4ED8", bg: "#DBEAFE", icon: <Briefcase size={14} />, label: "Jobs" },
  loans: { color: "#0F7A4E", bg: "#D1FAE5", icon: <IndianRupee size={14} />, label: "Loans" },
};

const STATUS_STYLE = {
  "New": { bg: "#EFF6FF", fg: "#1E3A8A" },
  "Contacted": { bg: "#DCFCE7", fg: "#166534" },
  "Not Contacted": { bg: "#FEE2E2", fg: "#991B1B" },
  "Customer Asked to Call Later": { bg: "#FEF9C3", fg: "#854D0E" },
  "Interested": { bg: "#F3E8FF", fg: "#6B21A8" },
  "Follow-up Required": { bg: "#FFEDD5", fg: "#9A3412" },
  "Converted": { bg: "#DCFCE7", fg: "#14532D" },
  "Lost": { bg: "#F1F5F9", fg: "#334155" },
};

const REQUIRES_FOLLOWUP = new Set(["Contacted", "Not Contacted", "Customer Asked to Call Later", "Follow-up Required"]);

const STATUSES = [
  "New", "Contacted", "Not Contacted", "Customer Asked to Call Later",
  "Interested", "Follow-up Required", "Converted", "Lost"
];

const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("asg_token")}` } });

// Normalize an Indian mobile to the E.164-ish form expected by wa.me (no plus, digits only).
// Accepts inputs like "9876543210", "+919876543210", "91-9876543210", "(91) 98765 43210" etc.
function waNumber(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return "91" + digits;       // bare 10-digit → assume India
  if (digits.length === 11 && digits.startsWith("0")) return "91" + digits.slice(1); // 0XXXXXXXXXX
  if (digits.length === 12 && digits.startsWith("91")) return digits; // already has 91
  return digits; // anything else, hand raw digits to wa.me
}

// Vertical-aware default greeting so clicking the icon opens WhatsApp Web with a ready-to-send message.
function waPrefillMessage(lead) {
  const name = lead?.name ? ` ${lead.name}` : "";
  const subject = lead?.subject ? ` regarding "${lead.subject}"` : "";
  const vertical = lead?.vertical;
  if (vertical === "yatra") return `Namaste${name}, this is Arth Setuu Global — following up on your yatra enquiry${subject}. May I share the latest details with you?`;
  if (vertical === "jobs")  return `Hello${name}, this is Arth Setuu Global HR${subject}. Is this a good time to discuss your application?`;
  if (vertical === "loans") return `Hello${name}, this is Arth Setuu Global — following up on your loan enquiry${subject}. Shall I walk you through the next steps?`;
  return `Hello${name}, this is Arth Setuu Global — following up on your recent enquiry${subject}.`;
}

function waLink(mobile, message) {
  const n = waNumber(mobile);
  if (!n) return "#";
  return `https://wa.me/${n}?text=${encodeURIComponent(message || "")}`;
}

export default function CRMTab() {
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vertical, setVertical] = useState("all");
  const [status, setStatus] = useState("");
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [managingTpls, setManagingTpls] = useState(false);

  const loadDashboard = async () => {
    try { const r = await axios.get(`${API}/admin/crm/dashboard`, authH()); setDashboard(r.data); }
    catch (e) { /* ignore */ }
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ vertical, status, filter, search, date_from: dateFrom, date_to: dateTo });
      const r = await axios.get(`${API}/admin/crm/leads?${params}`, authH());
      setLeads(r.data);
    } catch (e) { setLeads([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadDashboard(); }, []);
  useEffect(() => { loadLeads(); }, [vertical, status, filter, dateFrom, dateTo]);

  const applyDatePreset = (preset) => {
    const now = new Date(); const today = now.toISOString().slice(0, 10);
    const dOffset = (d) => new Date(now.getTime() - d * 86400000).toISOString().slice(0, 10);
    if (preset === "today") { setDateFrom(today); setDateTo(today); }
    else if (preset === "yesterday") { setDateFrom(dOffset(1)); setDateTo(dOffset(1)); }
    else if (preset === "week") { setDateFrom(dOffset(6)); setDateTo(today); }
    else if (preset === "month") { setDateFrom(dOffset(29)); setDateTo(today); }
    else { setDateFrom(""); setDateTo(""); }
  };

  const overall = dashboard?.overall || {};

  return (
    <div className="crm" data-testid="crm-tab">
      <div className="crm-head">
        <div>
          <h2>Arth Setuu Global · CRM</h2>
          <p>One dashboard across Yatra, Jobs & Loans — leads, follow-ups and activity in one place.</p>
        </div>
        <button className="crm-refresh" onClick={() => { loadDashboard(); loadLeads(); }} data-testid="crm-refresh">
          <Loader2 size={14} /> Refresh
        </button>
      </div>

      <div className="crm-actions" data-testid="crm-actions">
        <button className="crm-create-btn" onClick={() => setCreating(true)} data-testid="crm-create-lead">
          + Create Lead
        </button>
        <button className="crm-tpl-btn" onClick={() => setManagingTpls(true)} data-testid="crm-manage-templates">
          <MessageCircle size={14} /> Manage WhatsApp Templates
        </button>
      </div>

      <div className="crm-metrics" data-testid="crm-metrics">
        <MetricCard testid="metric-total" icon={<Users size={16} />} label="Total leads" value={overall.total} accent="#0B1220" />
        <MetricCard testid="metric-new" icon={<Sparkles size={16} />} label="New" value={overall.new} accent="#1E3A8A" note={`${overall.new_today || 0} today`} />
        <MetricCard testid="metric-contacted" icon={<CheckCircle2 size={16} />} label="Contacted" value={overall.contacted} accent="#166534" />
        <MetricCard testid="metric-pending" icon={<Calendar size={16} />} label="Pending follow-ups" value={overall.pending_followups} accent="#B45309" />
      </div>

      {(overall.overdue_followups > 0 || overall.today_followups > 0) && (
        <div className="crm-alerts" data-testid="crm-alerts">
          {overall.overdue_followups > 0 && (
            <button className="crm-alert overdue" onClick={() => { setFilter("overdue"); setVertical("all"); }} data-testid="alert-overdue">
              <AlertTriangle size={18} />
              <div><b>{overall.overdue_followups} overdue follow-up{overall.overdue_followups === 1 ? "" : "s"}</b><span>Click to review</span></div>
              <ChevronRight size={16} />
            </button>
          )}
          {overall.today_followups > 0 && (
            <button className="crm-alert today" onClick={() => { setFilter("today"); setVertical("all"); }} data-testid="alert-today">
              <Clock size={18} />
              <div><b>{overall.today_followups} follow-up{overall.today_followups === 1 ? "" : "s"} due today</b><span>Click to open</span></div>
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      )}

      <div className="crm-verticals" data-testid="crm-per-vertical">
        {["yatra", "jobs", "loans"].map(v => (
          <VerticalCard key={v} vertical={v} data={dashboard?.[v]} onOpen={() => { setVertical(v); setFilter(""); }} />
        ))}
      </div>

      <div className="crm-toolbar" data-testid="crm-toolbar">
        <div className="crm-chips">
          {["all", "yatra", "jobs", "loans"].map(v => (
            <button
              key={v}
              className={`crm-chip ${vertical === v ? "active" : ""}`}
              onClick={() => setVertical(v)}
              data-testid={`crm-vertical-${v}`}
            >{v === "all" ? "All verticals" : VERTICAL_STYLE[v].label}</button>
          ))}
        </div>
        <div className="crm-chips">
          {[["", "All time"], ["today", "Today"], ["yesterday", "Yesterday"], ["week", "This week"], ["month", "This month"]].map(([p, l]) => (
            <button key={p} className="crm-chip" onClick={() => applyDatePreset(p)} data-testid={`crm-date-${p || "all"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="crm-toolbar" style={{ marginTop: 12 }}>
        <div className="crm-search">
          <Search size={16} color="#64748B" />
          <input placeholder="Search by name, mobile or email" value={search} onChange={e => setSearch(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && loadLeads()} data-testid="crm-search-input" />
          <button onClick={loadLeads} data-testid="crm-search-btn">Search</button>
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="crm-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="crm-followup-filter">
          <option value="">All follow-ups</option>
          <option value="overdue">Overdue</option>
          <option value="today">Today</option>
          <option value="upcoming">Upcoming</option>
        </select>
      </div>

      <div className="crm-lead-list" data-testid="crm-lead-list">
        {loading && <div className="crm-empty"><Loader2 size={22} /> Loading leads…</div>}
        {!loading && leads.length === 0 && <div className="crm-empty">No leads match these filters.</div>}
        {!loading && leads.map(l => (
          <div key={`${l.vertical}-${l.id}`} className={`crm-lead ${l.is_overdue ? "overdue" : ""}`}
                  role="button" tabIndex={0}
                  onClick={() => setSelected(l)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(l); } }}
                  data-testid={`crm-lead-${l.id}`}>
            <span className="crm-lead-tag" style={{ background: VERTICAL_STYLE[l.vertical].bg, color: VERTICAL_STYLE[l.vertical].color }}>
              {VERTICAL_STYLE[l.vertical].icon} {VERTICAL_STYLE[l.vertical].label}
            </span>
            <div className="crm-lead-main">
              <b>{l.name || "—"}</b>
              <span className="crm-lead-sub">{l.mobile} · {l.subject || "—"}</span>
            </div>
            <span className="crm-lead-status" style={{ background: STATUS_STYLE[l.status]?.bg, color: STATUS_STYLE[l.status]?.fg }}>{l.status}</span>
            <div className="crm-lead-followup">
              {l.next_followup_date ? (
                <span className={l.is_overdue ? "overdue" : ""}>
                  <Calendar size={12} /> {l.next_followup_date} {l.next_followup_time || ""}
                  {l.is_overdue && <b> · overdue</b>}
                </span>
              ) : <span className="muted">No follow-up</span>}
            </div>
            {l.mobile && (
              <WhatsAppLauncher lead={l} />
            )}
            <ChevronRight size={16} color="#94a3b8" />
          </div>
        ))}
      </div>

      {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} onSaved={() => { loadLeads(); loadDashboard(); }} />}
      {creating && <CreateLeadModal onClose={() => setCreating(false)} onCreated={() => { loadLeads(); loadDashboard(); }} />}
      {managingTpls && <WATemplatesManager onClose={() => setManagingTpls(false)} />}
    </div>
  );
}

function MetricCard({ icon, label, value, note, accent, testid }) {
  return (
    <div className="crm-metric" data-testid={testid} style={{ "--_c": accent }}>
      <span className="crm-metric-icon">{icon}</span>
      <b>{value === undefined ? "—" : value}</b>
      <small>{label}</small>
      {note && <em>{note}</em>}
    </div>
  );
}

function VerticalCard({ vertical, data, onOpen }) {
  const style = VERTICAL_STYLE[vertical]; const d = data || {};
  return (
    <button className="crm-vc" onClick={onOpen} data-testid={`crm-vc-${vertical}`}
            style={{ "--_c": style.color, "--_bg": style.bg }}>
      <div className="crm-vc-head">
        <span className="crm-vc-badge">{style.icon} {style.label.toUpperCase()}</span>
        <ChevronRight size={16} />
      </div>
      <div className="crm-vc-grid">
        <div><b>{d.total ?? 0}</b><small>Total</small></div>
        <div><b>{d.new ?? 0}</b><small>New</small></div>
        <div><b>{d.contacted ?? 0}</b><small>Contacted</small></div>
      </div>
      <div className="crm-vc-today">
        <span><Calendar size={12} /> {d.today_followups ?? 0} today</span>
        <span className={d.overdue_followups > 0 ? "warn" : ""}>
          <AlertTriangle size={12} /> {d.overdue_followups ?? 0} overdue
        </span>
      </div>
    </button>
  );
}

function LeadDrawer({ lead: initial, onClose, onSaved }) {
  const [lead, setLead] = useState(initial);
  const [activities, setActivities] = useState([]);
  const [form, setForm] = useState({
    status: initial.status,
    notes: initial.notes || "",
    next_followup_date: initial.next_followup_date || "",
    next_followup_time: initial.next_followup_time || "",
    activity_note: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const loadDetail = async () => {
    try {
      const r = await axios.get(`${API}/admin/crm/leads/${lead.vertical}/${lead.id}`, authH());
      setLead(r.data.lead);
      setActivities(r.data.activities || []);
      setForm(f => ({ ...f, status: r.data.lead.status, notes: r.data.lead.notes || "",
                     next_followup_date: r.data.lead.next_followup_date || "",
                     next_followup_time: r.data.lead.next_followup_time || "" }));
    } catch { /* ignore */ }
  };

  useEffect(() => { loadDetail(); }, []);

  const requiresFollowup = REQUIRES_FOLLOWUP.has(form.status);
  const missingFollowup = requiresFollowup && (!form.next_followup_date || !form.next_followup_time);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setErr(""); setMsg("");
    if (missingFollowup) { setErr("Please select the next follow-up date and time before saving this status."); return; }
    setSaving(true);
    try {
      const body = { ...form };
      await axios.patch(`${API}/admin/crm/leads/${lead.vertical}/${lead.id}`, body, authH());
      setMsg("Saved. Activity logged.");
      setForm(f => ({ ...f, activity_note: "" }));
      await loadDetail();
      onSaved?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const vstyle = VERTICAL_STYLE[lead.vertical];

  return (
    <div className="crm-drawer-backdrop" onClick={onClose} data-testid="crm-drawer">
      <div className="crm-drawer" onClick={e => e.stopPropagation()}>
        <button className="crm-drawer-close" onClick={onClose} data-testid="crm-drawer-close"><X size={18} /></button>

        <div className="crm-drawer-head">
          <span className="crm-lead-tag" style={{ background: vstyle.bg, color: vstyle.color }}>{vstyle.icon} {vstyle.label}</span>
          <h3>{lead.name || "—"}</h3>
          <div className="crm-drawer-subj">{lead.subject}</div>
          <div className="crm-drawer-contacts">
            {lead.mobile && <a href={`tel:${lead.mobile}`} data-testid="crm-lead-call"><Phone size={13} /> {lead.mobile}</a>}
            {lead.mobile && <a href={waLink(lead.mobile, waPrefillMessage(lead))} target="_blank" rel="noreferrer" data-testid="crm-lead-wa"><MessageCircle size={13} /> WhatsApp</a>}
            {lead.email && <a href={`mailto:${lead.email}`} data-testid="crm-lead-email"><Mail size={13} /> {lead.email}</a>}
            {lead.city && <span><MapPin size={12} /> {lead.city}</span>}
          </div>
          <div className="crm-drawer-meta">
            <span><b>ID:</b> {lead.id}</span>
            <span><b>Source:</b> {lead.source}</span>
            <span><b>Created:</b> {(lead.created_at || "").slice(0, 16).replace("T", " ")}</span>
          </div>
        </div>

        <section className="crm-block">
          <h4>Update lead</h4>
          {err && <div className="crm-form-error" data-testid="crm-form-error">{err}</div>}
          {msg && <div className="crm-form-ok">{msg}</div>}

          <label>Status</label>
          <select value={form.status} onChange={e => set("status", e.target.value)} data-testid="crm-status-select">
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {requiresFollowup && (
            <div className="crm-followup-req" data-testid="crm-followup-required">
              <b>Follow-up is mandatory for this status.</b>
              <span>Set the next date and time to save.</span>
            </div>
          )}

          <div className="crm-form-row">
            <div>
              <label>Next follow-up date {requiresFollowup && <em>· required</em>}</label>
              <input type="date" value={form.next_followup_date} onChange={e => set("next_followup_date", e.target.value)}
                     data-testid="crm-followup-date" required={requiresFollowup} />
            </div>
            <div>
              <label>Next follow-up time {requiresFollowup && <em>· required</em>}</label>
              <input type="time" value={form.next_followup_time} onChange={e => set("next_followup_time", e.target.value)}
                     data-testid="crm-followup-time" required={requiresFollowup} />
            </div>
          </div>

          <label>Follow-up notes / activity</label>
          <textarea placeholder="e.g. Customer travelling this week — call after Monday"
                    value={form.activity_note} onChange={e => set("activity_note", e.target.value)} data-testid="crm-activity-note" />

          <label>Persistent notes on lead</label>
          <textarea placeholder="Anything to remember about this lead" value={form.notes}
                    onChange={e => set("notes", e.target.value)} data-testid="crm-notes" />

          <button className="crm-save" onClick={save} disabled={saving || missingFollowup} data-testid="crm-save">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </section>

        <section className="crm-block">
          <h4>Enquiry details</h4>
          <div className="crm-detail-list" data-testid="crm-details">
            {Object.entries(lead.details || {}).map(([k, v]) => (
              (v || v === 0) ? (
                <div key={k}>
                  <span className="crm-detail-label">{k.replace(/_/g, " ")}</span>
                  {k === "resume_path" ? (
                    <a className="crm-detail-value link" href={`${API}/admin/resume/${lead.id}?token=${localStorage.getItem("asg_token")}`}
                       target="_blank" rel="noreferrer" data-testid="crm-resume-download">
                      <FileText size={13} /> Download resume
                    </a>
                  ) : (
                    <span className="crm-detail-value">{String(v)}</span>
                  )}
                </div>
              ) : null
            ))}
          </div>
        </section>

        <section className="crm-block">
          <h4>Activity timeline</h4>
          <ol className="crm-timeline" data-testid="crm-timeline">
            {activities.length === 0 && <li className="empty">No activity yet — save an update above to begin the timeline.</li>}
            {activities.map(a => (
              <li key={a.id} data-testid={`crm-activity-${a.id}`}>
                <div className="crm-timeline-dot" />
                <div>
                  <b>{fmtActivityTitle(a)}</b>
                  {a.note && <p>{a.note}</p>}
                  <small>{(a.created_at || "").slice(0, 16).replace("T", " ")} · {a.created_by || "system"}</small>
                </div>
              </li>
            ))}
            <li>
              <div className="crm-timeline-dot start" />
              <div><b>Lead created</b><small>{(lead.created_at || "").slice(0, 16).replace("T", " ")} · via {lead.source}</small></div>
            </li>
          </ol>
        </section>
      </div>
    </div>
  );
}

function fmtActivityTitle(a) {
  if (a.type === "status_change") return `Status: ${a.from_status || "—"} → ${a.to_status || "—"}`;
  if (a.type === "follow_up_set") return `Follow-up scheduled${a.followup_date ? ` for ${a.followup_date} ${a.followup_time || ""}` : ""}`;
  if (a.type === "note") return "Note added";
  return a.type;
}


// ---------- WhatsApp Launcher (popover with template picker) ----------
function WhatsAppLauncher({ lead }) {
  const [open, setOpen] = useState(false);
  const [tpls, setTpls] = useState(null); // null = not fetched yet
  const [busyId, setBusyId] = useState("");

  const loadTemplates = async () => {
    if (tpls !== null) return;
    try {
      const r = await axios.get(`${API}/admin/whatsapp-templates?vertical=${lead.vertical || "all"}`, authH());
      setTpls(r.data || []);
    } catch { setTpls([]); }
  };

  useEffect(() => {
    if (!open) return;
    loadTemplates();
    const onDoc = (e) => {
      if (!e.target.closest?.(`[data-wa-launcher-id="${lead.id}"]`)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]); // eslint-disable-line

  const sendWith = async (tpl) => {
    setBusyId(tpl.id);
    try {
      const r = await axios.post(`${API}/admin/whatsapp-templates/${tpl.id}/render`, {
        name: lead.name, mobile: lead.mobile, city: lead.city, subject: lead.subject, vertical: lead.vertical,
      }, authH());
      const msg = r.data?.rendered || tpl.body;
      window.open(waLink(lead.mobile, msg), "_blank", "noopener");
    } catch {
      // Fallback: still open WhatsApp with the raw body
      window.open(waLink(lead.mobile, tpl.body), "_blank", "noopener");
    }
    setBusyId("");
    setOpen(false);
  };

  const sendDefault = () => {
    window.open(waLink(lead.mobile, waPrefillMessage(lead)), "_blank", "noopener");
    setOpen(false);
  };

  return (
    <div className="crm-wa-wrap" data-wa-launcher-id={lead.id} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="crm-lead-wa-icon"
        title={`WhatsApp ${lead.name || lead.mobile}`}
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        data-testid={`crm-lead-wa-icon-${lead.id}`}
      >
        <MessageCircle size={16} />
      </button>
      {open && (
        <div className="crm-wa-pop" role="menu" data-testid={`crm-wa-pop-${lead.id}`}>
          <div className="crm-wa-pop-head">
            <b>Send WhatsApp to {lead.name || lead.mobile}</b>
            <small>Pick a template — opens WhatsApp Web with your logged-in number.</small>
          </div>
          <div className="crm-wa-pop-body">
            {tpls === null && <div className="crm-wa-empty"><Loader2 size={14} /> Loading templates…</div>}
            {tpls && tpls.length === 0 && (
              <div className="crm-wa-empty">No templates yet. Use "Manage WhatsApp Templates" above to add some.</div>
            )}
            {tpls && tpls.map(t => (
              <button
                key={t.id}
                type="button"
                className="crm-wa-tpl"
                disabled={busyId === t.id}
                onClick={() => sendWith(t)}
                data-testid={`crm-wa-tpl-${t.id}`}
              >
                <span className={`crm-wa-tag ${t.vertical}`}>{t.vertical === "all" ? "GENERIC" : t.vertical.toUpperCase()}</span>
                <b>{t.title}</b>
                <small>{t.body.length > 120 ? t.body.slice(0, 120) + "…" : t.body}</small>
              </button>
            ))}
          </div>
          <div className="crm-wa-pop-foot">
            <button type="button" className="crm-wa-blank" onClick={sendDefault} data-testid={`crm-wa-blank-${lead.id}`}>
              Skip template — open blank chat
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Admin: Manage WhatsApp Templates ----------
const EMPTY_TPL = { title: "", body: "", vertical: "yatra", active: true };

function WATemplatesManager({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // current draft (new or existing)
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/whatsapp-templates/all`, authH());
      setItems(r.data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    if (!editing.title.trim() || !editing.body.trim()) { setErr("Title and body are required."); return; }
    try {
      const payload = { title: editing.title, body: editing.body, vertical: editing.vertical, active: editing.active };
      if (editing.id) await axios.patch(`${API}/admin/whatsapp-templates/${editing.id}`, payload, authH());
      else            await axios.post(`${API}/admin/whatsapp-templates`, payload, authH());
      setEditing(null);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed."); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.title}"?`)) return;
    try { await axios.delete(`${API}/admin/whatsapp-templates/${t.id}`, authH()); await load(); }
    catch { /* ignore */ }
  };

  const grouped = useMemo(() => {
    const map = { yatra: [], jobs: [], loans: [], all: [] };
    items.forEach(t => { (map[t.vertical] || map.all).push(t); });
    return map;
  }, [items]);

  const groupLabels = { yatra: "Yatra", jobs: "Jobs", loans: "Loans", all: "Generic (all verticals)" };

  return (
    <div className="crm-drawer-backdrop" onClick={onClose} data-testid="wa-templates-modal">
      <div className="crm-drawer wa-tpl-drawer" onClick={e => e.stopPropagation()}>
        <button className="crm-drawer-close" onClick={onClose} data-testid="wa-templates-close"><X size={18} /></button>
        <div className="crm-drawer-head">
          <span className="crm-lead-tag" style={{ background: "#DCFCE7", color: "#0F7A4E" }}><MessageCircle size={12} /> WHATSAPP</span>
          <h3>Manage WhatsApp Templates</h3>
          <div className="crm-drawer-subj">Save canned replies per vertical. Use <code>{"{name}"}</code>, <code>{"{subject}"}</code>, <code>{"{city}"}</code>, <code>{"{mobile}"}</code>, <code>{"{vertical}"}</code> as placeholders.</div>
        </div>

        <div className="wa-tpl-actions">
          <button className="btn btn-primary btn-sm" onClick={() => setEditing({ ...EMPTY_TPL })} data-testid="wa-tpl-add">
            + New template
          </button>
        </div>

        {loading && <div className="crm-empty"><Loader2 size={22} /> Loading…</div>}

        {!loading && ["yatra", "jobs", "loans", "all"].map(v => (
          <section key={v} className="wa-tpl-group" data-testid={`wa-tpl-group-${v}`}>
            <h4>{groupLabels[v]} <small>({grouped[v]?.length || 0})</small></h4>
            {(!grouped[v] || grouped[v].length === 0) && <div className="crm-wa-empty">No templates in this group yet.</div>}
            {grouped[v]?.map(t => (
              <div key={t.id} className="wa-tpl-row" data-testid={`wa-tpl-row-${t.id}`}>
                <div className="wa-tpl-row-main">
                  <b>{t.title}</b>
                  {!t.active && <span className="wa-tpl-pill inactive">Inactive</span>}
                  <p>{t.body}</p>
                </div>
                <div className="wa-tpl-row-actions">
                  <button className="asg-mini-btn" onClick={() => setEditing({ ...t })} data-testid={`wa-tpl-edit-${t.id}`}>Edit</button>
                  <button className="asg-mini-btn danger" onClick={() => remove(t)} data-testid={`wa-tpl-delete-${t.id}`}>Delete</button>
                </div>
              </div>
            ))}
          </section>
        ))}

        {editing && (
          <div className="wa-tpl-editor" data-testid="wa-tpl-editor" onClick={e => e.stopPropagation()}>
            <div className="wa-tpl-editor-inner">
              <div className="wa-tpl-editor-head">
                <b>{editing.id ? "Edit template" : "New template"}</b>
                <button className="crm-drawer-close" onClick={() => setEditing(null)}><X size={16} /></button>
              </div>
              {err && <div className="crm-form-error">{err}</div>}
              <label>Vertical
                <select value={editing.vertical} onChange={e => setEditing({ ...editing, vertical: e.target.value })} data-testid="wa-tpl-vertical">
                  <option value="yatra">Yatra</option>
                  <option value="jobs">Jobs</option>
                  <option value="loans">Loans</option>
                  <option value="all">Generic (all verticals)</option>
                </select>
              </label>
              <label>Title
                <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. Booking Nudge" data-testid="wa-tpl-title" />
              </label>
              <label>Message body
                <textarea rows={6} value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })}
                          placeholder="Hi {name}, ..." data-testid="wa-tpl-body" />
              </label>
              <label style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={!!editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} data-testid="wa-tpl-active" />
                <span>Active (shows in the CRM picker)</span>
              </label>
              <div className="wa-tpl-editor-foot">
                <button className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={save} data-testid="wa-tpl-save">{editing.id ? "Save changes" : "Create template"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
